// src/api/content.js
// Content API using local JSON files
import contentService from '../services/contentService';

/**
 * Get content detail by type and ID
 * @param {string} type - Content type (blog, youtube, external_link)
 * @param {number|string} id - Content ID
 * @returns {Promise<Object>} Content detail
 */
export const getContentDetail = async (type, id) => {
  try {
    const content = await contentService.getContentByTypeAndId(type, id);
    if (!content) {
      throw new Error(`Content not found: ${type}/${id}`);
    }
    return content;
  } catch (error) {
    console.error(`Content detail fetch failed (${type}/${id}):`, error);
    throw error;
  }
};

/**
 * Get blog detail by ID
 * @param {number|string} id - Blog ID
 * @returns {Promise<Object>} Blog detail
 */
export const getBlogDetail = async (id) => {
  return getContentDetail('blog', id);
};

/**
 * Get all published content for homepage
 * Returns published content ordered by updated_at DESC
 * @returns {Promise<Object>} Ordered content list
 */
export const getHomeOrderedContent = async () => {
  try {
    const contents = await contentService.getPublishedContent();

    // Transform to the expected format with items array
    const items = contents.map((content, index) => ({
      content: {
        id: content.id,
        uniqueId: `${content.content_type}_${content.id}`,
        title: content.title,
        title_en: content.title_en,
        content_type: content.content_type,
        image_path: content.image_path,
        youtube_video_id: content.youtube_video_id,
        external_url: content.external_url,
        created_at: content.created_at,
        updated_at: content.updated_at
      },
      order: index
    }));

    return { items };
  } catch (error) {
    console.error('Homepage content list fetch failed:', error);
    throw error;
  }
};

/**
 * Get content for a specific section
 * Returns published content ordered by updated_at DESC
 * @param {string} section - Section name
 * @returns {Promise<Object>} Ordered content list
 */
export const getSectionOrderedContent = async (section) => {
  try {
    const contents = await contentService.getPublishedContent();

    // Transform to the expected format with items array
    const items = contents.map((content, index) => ({
      content: {
        id: content.id,
        uniqueId: `${content.content_type}_${content.id}`,
        title: content.title,
        title_en: content.title_en,
        content_type: content.content_type,
        image_path: content.image_path,
        youtube_video_id: content.youtube_video_id,
        external_url: content.external_url,
        created_at: content.created_at,
        updated_at: content.updated_at
      },
      order: index
    }));

    return { items };
  } catch (error) {
    console.error(`${section} section content list fetch failed:`, error);
    throw error;
  }
};

/**
 * Get content by type (blog, youtube, external_link)
 * @param {string} contentType - Content type
 * @returns {Promise<Array>} Content list
 */
export const getContentByType = async (contentType) => {
  try {
    return await contentService.getPublishedContentByType(contentType);
  } catch (error) {
    console.error(`Content by type fetch failed (${contentType}):`, error);
    throw error;
  }
};

/**
 * Fetch details for multiple content items in parallel
 * @param {Array<Object>} items - Content items array
 * @returns {Promise<Object>} Content details keyed by uniqueId
 */
export const fetchAllContentDetails = async (items) => {
  try {
    console.log('Fetching details for', items.length, 'content items');

    const promises = items.map(item => {
      const { content_type, id } = item.content;
      return contentService.getContentByTypeAndId(content_type, id);
    });

    const results = await Promise.allSettled(promises);

    const tempDetails = {};
    items.forEach((item, index) => {
      if (results[index].status === 'fulfilled' && results[index].value) {
        const uniqueId = item.content.uniqueId || `${item.content.content_type}_${item.content.id}`;
        tempDetails[uniqueId] = results[index].value;
      }
    });

    return tempDetails;
  } catch (error) {
    console.error('Batch content details fetch failed:', error);
    throw error;
  }
};

const contentAPI = {
  getContentDetail,
  getBlogDetail,
  getHomeOrderedContent,
  getSectionOrderedContent,
  getContentByType,
  fetchAllContentDetails
};

export default contentAPI;
